<template>
  <section class="unauthorized-panel" role="alert" aria-live="polite">
    <div class="unauthorized-icon">
      <b-icon icon="lock" size="is-large" type="is-danger" />
    </div>
    <h2 class="unauthorized-title">
      {{ $t("unauthorizedApplication.title") }}
    </h2>
    <p class="unauthorized-identity">
      <span v-html="$t('unauthorizedApplication.identity', { user: userLogin })" />
    </p>
    <p class="unauthorized-cause">
      <span v-html="$t('unauthorizedApplication.cause', { application: applicationName })" />
    </p>
    <p class="unauthorized-consequence">
      {{ $t("unauthorizedApplication.consequence") }}
    </p>
    <p class="unauthorized-action">
      {{ $t("unauthorizedApplication.action") }}
    </p>
    <div class="unauthorized-buttons">
      <b-button
        type="is-primary"
        icon-left="arrow-left"
        @click="goBack"
      >
        {{ $t("unauthorizedApplication.back") }}
      </b-button>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * Placeholder affiché en lieu et place du contenu d'une application
 * quand l'utilisateur connecté n'a aucun rôle effectif sur celle-ci
 * ( {@code applicationRoles == null || []} et pas {@code openAdomAdmin} ) .
 *
 * <p><b>Pourquoi</b> ( ticket bug ticket_507 ) : un utilisateur sans
 * rôle pouvait coller l'URL {@code /applications/<name>} et atterrir
 * sur la page d'accueil sans aucun signal . Les sous-routes
 * crashaient en HTTP 500 ( cf {@code AuthorizationFilter} +
 * {@code ApplicationRepository.findApplication} sous RLS ) , donnant
 * l'impression d'un bug silencieux .
 *
 * <p><b>Design SOLID</b> : composant purement présentationnel
 * ( props uniquement , aucune dépendance store / fetch ) . Le check
 * d'éligibilité vit dans le helper pur
 * {@code applicationAccessGuard.hasAnyApplicationAccess} - le parent
 * ( typiquement {@code ApplicationInfoView} ) décide quand monter
 * ce composant en se basant sur ce helper .
 */
import { useRouter } from "vue-router";
import { BButton, BIcon } from "buefy";

defineProps<{
  applicationName: string;
  userLogin: string;
}>();

const router = useRouter();

function goBack(): void {
  void router.push({ name: "Applications" });
}
</script>

<style scoped>
.unauthorized-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  max-width: 720px;
  margin: 2rem auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.unauthorized-icon {
  margin-bottom: 0.5rem;
}

.unauthorized-title {
  font-family: var(--font-title, inherit);
  font-size: 1.6rem;
  font-weight: 700;
  color: #c0392b;
  margin: 0;
}

.unauthorized-identity,
.unauthorized-cause,
.unauthorized-consequence,
.unauthorized-action {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: #374151;
}

.unauthorized-cause :deep(strong),
.unauthorized-identity :deep(strong) {
  color: #111827;
}

.unauthorized-action {
  color: #6b7280;
  font-style: italic;
  margin-top: 0.5rem;
}

.unauthorized-buttons {
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
}
</style>
